import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { MessageCircleWarning } from 'lucide-vue-next';
import { emitToast } from '@/shared/lib/toast';
import CreateTicketModal from './CreateTicketModal.vue';

const dataTest = 'global-support-fab';
// Пометка беты входит в aria-label: aria-label на кнопке перекрывает её
// содержимое, а сам бейдж скрыт от скринридера как декоративный.
const ariaLabel = 'Сообщить о проблеме (бета)';

/**
 * Пути, на которых FAB прячется:
 *  - /admin/* — операторская/админская зона
 *  - /login, /register — auth
 *  - /MeetingPage, /conference, /meeting, /booking-conference, /event-conference —
 *    видеовстречи (там полноэкранный UI и контекстная ReportProblemButton)
 *  - /support — там пользователь уже в саппорте, дублировать FAB не нужно
 *  - корень "/" и лэндинги Home/SuperpeerLanding — landing pages, чтобы не
 *    мешать первой странице
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'GlobalSupportFab',
  setup(__props) {

/**
 * GlobalSupportFab — глобальный плавающий FAB справа внизу на всех
 * страницах сайта (кроме админки/auth/landing/in-call).
 *
 * Это «всегда видимая» альтернатива контекстной <ReportProblemButton>:
 * на consultation/meeting страницах живёт ReportProblemButton с
 * автоконтекстом, а здесь — простой ввод без контекста для общих
 * обращений (вопросы об аккаунте, бронированиях, оплате и т.п.).
 *
 * Условия отображения:
 *   - featureFlag VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED === 'true'
 *   - пользователь авторизован
 *   - текущий путь не в EXCLUDED_PREFIXES (админка, auth, лэндинги, видеовстреча)
 */
const route = useRoute();
const router = useRouter();
const store = useStore();

const showModal = ref<boolean>(false);
const EXCLUDED_PREFIXES = [
    '/admin',
    '/login',
    '/register',
    '/MeetingPage',
    '/conference',
    '/booking-conference',
    '/event-conference',
    '/support',
] as const;

const EXCLUDED_EXACT = ['/', '/Home', '/NewLandingMentor', '/SuperpeerLanding'] as const;

const featureFlagEnabled = computed<boolean>(() => {
    // Прямой `process.env.X` — webpack DefinePlugin подменит литералом.
    // globalThis-cast в browser runtime даёт undefined и сбивает webpack.
    const raw = process.env.VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED ?? '';
    return String(raw).toLowerCase() === 'true';
});

const isAuthenticated = computed<boolean>(() => Boolean(store.getters?.isAuthenticated));

const isExcludedPath = computed<boolean>(() => {
    const path = route.path;
    if (EXCLUDED_EXACT.includes(path as (typeof EXCLUDED_EXACT)[number])) return true;
    return EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
});

const shouldRender = computed<boolean>(
    () => featureFlagEnabled.value && isAuthenticated.value && !isExcludedPath.value,
);

const onClick = (): void => {
    showModal.value = true;
};

const onCreated = (_payload: { publicId: string; subject: string }): void => {
    // CreateTicketModal уже показал toast про создание; повторно не дублируем.
};

const onDuplicate = (payload: { publicId: string; subject: string }): void => {
    emitToast('У вас уже есть обращение по этому вопросу.', 'info');
    router.push({ name: 'support-ticket', params: { publicId: payload.publicId } }).catch(() => {
        /* ignore — навигация может не пройти, тоста достаточно */
    });
};

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (shouldRender.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "global-support-fab",
          "data-test": dataTest
        }, [
          _createVNode(_component_v_btn, {
            icon: "",
            size: "large",
            color: "primary",
            elevation: "6",
            class: "global-support-fab__btn",
            "aria-label": ariaLabel,
            title: ariaLabel,
            onClick: onClick
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(MessageCircleWarning), { size: 22 })
            ]),
            _: 1
          }),
          _createElementVNode("span", {
            class: "global-support-fab__beta",
            "aria-hidden": "true",
            onClick: onClick
          }, " BETA "),
          _createVNode(CreateTicketModal, {
            modelValue: showModal.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
            context: null,
            "preset-category": undefined,
            onCreated: onCreated,
            onDuplicate: onDuplicate
          }, null, 8, ["modelValue"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})